import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

/**
 * API base URL.
 * - Uses `VITE_API_BASE` from env when set (production points to the public host).
 * - Falls back to the dev backend on `http://localhost:5090`.
 * - An explicit empty string keeps requests same-origin (relative URLs), useful
 *   when the web app is served by the API itself.
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:5090'

const TOKEN_KEY = 'ufuqerp_token'
const REFRESH_KEY = 'ufuqerp_refresh_token'
const USER_KEY = 'ufuqerp_user'

interface RetriableConfig extends InternalAxiosRequestConfig {
  /** Set when the response interceptor has retried the request once already. */
  _retry?: boolean
  /** How many transient-error retries have been attempted for this request. */
  _retryCount?: number
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  // Allow callers (TanStack Query, route loaders) to pass their own AbortSignal.
  // Axios honours `signal` on the request config automatically.
})

// ─── Request interceptor: inject Bearer token ────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// ─── Response interceptor: refresh on 401, retry transient 5xx/network ───
let refreshInFlight: Promise<string | null> | null = null
const refreshSubscribers: Array<(token: string | null) => void> = []

function onRefreshed(token: string | null) {
  while (refreshSubscribers.length) refreshSubscribers.shift()?.(token)
}

function logoutAndRedirect() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(USER_KEY)
  // اشتراك تنبيهات سطح المكتب لهذا المتصفح يُفكّ مع انتهاء الجلسة (يسمعه webPush.ts)
  window.dispatchEvent(new Event('ufuq-logout'))
  const target = '/login'
  if (!window.location.pathname.startsWith(target)) {
    window.location.href = target
  }
}

async function tryRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_KEY)
  if (!refreshToken) return null
  try {
    // Use raw axios (not the wrapped instance) to avoid triggering this same
    // interceptor recursively if the refresh endpoint itself returns 401.
    const res = await axios.post(
      `${API_BASE}/api/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    )
    const newToken: string | undefined = res.data?.token
    const newRefresh: string | undefined = res.data?.refreshToken
    if (!newToken) return null
    localStorage.setItem(TOKEN_KEY, newToken)
    if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh)
    return newToken
  } catch {
    return null
  }
}

/** ينتهي التوكن خلال [marginMs] أو انتهى — يُقرأ `exp` من حمولة JWT بلا تحقق (للتوقيت فقط) */
function expiresSoon(jwt: string, marginMs = 60_000): boolean {
  try {
    const part = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const { exp } = JSON.parse(atob(part)) as { exp?: number }
    return typeof exp === 'number' && exp * 1000 - Date.now() < marginMs
  } catch {
    return false
  }
}

/**
 * توكنٌ صالح لاتصالٍ طويل الأمد (مركز الإشعارات الحيّ): إن انتهى أو قارب الانتهاء
 * يُجدَّد بصمت عبر مسار التجديد نفسه (طلبٌ واحد مشترك مع أي 401 متزامن) — فلا يعلق
 * الاتصال الحيّ بتوكنٍ منتهٍ بعد ساعات من فتح الصفحة.
 */
export async function freshAccessToken(): Promise<string> {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token || !expiresSoon(token)) return token ?? ''
  if (!refreshInFlight) {
    refreshInFlight = tryRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  const next = await refreshInFlight
  onRefreshed(next)
  return next ?? token
}

const MAX_RETRIES = 3
const RETRY_BASE_MS = 300

function isTransient(error: AxiosError): boolean {
  if (error.code === 'ECONNABORTED') return true
  if (!error.response) return true // network error
  const status = error.response.status
  return status === 502 || status === 503 || status === 504 || status === 408
}

function isCanceled(error: unknown): boolean {
  return axios.isCancel(error) || (error as AxiosError)?.code === 'ERR_CANCELED'
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined
    if (!config) return Promise.reject(error)
    if (isCanceled(error)) return Promise.reject(error)

    // ── 401 → silent refresh, then retry the original request once ─────
    if (error.response?.status === 401 && !config._retry) {
      config._retry = true

      // Multiple parallel 401s share one in-flight refresh attempt.
      if (!refreshInFlight) {
        refreshInFlight = tryRefresh().finally(() => {
          refreshInFlight = null
        })
      }

      const newToken = await refreshInFlight
      // Notify any other queued requests (they were stalled by `onRefreshed`).
      onRefreshed(newToken)

      if (!newToken) {
        logoutAndRedirect()
        return Promise.reject(error)
      }
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${newToken}`
      return api.request(config)
    }

    // ── Transient errors → exponential backoff retry ──────────────────
    if (isTransient(error)) {
      const count = (config._retryCount ?? 0) + 1
      if (count <= MAX_RETRIES) {
        config._retryCount = count
        const delay = RETRY_BASE_MS * 2 ** (count - 1)
        await new Promise((r) => setTimeout(r, delay))
        return api.request(config)
      }
    }

    return Promise.reject(error)
  },
)

/**
 * Helper for cancellable requests: returns the axios call AND the AbortController.
 * Use when you want to cancel from outside the request (route change, component unmount).
 *
 * For typical use, prefer passing `signal` directly:
 *   api.get('/x', { signal: abortController.signal })
 */
export function cancellable<T>(call: (config: AxiosRequestConfig) => Promise<T>) {
  const controller = new AbortController()
  const promise = call({ signal: controller.signal })
  return { promise, cancel: () => controller.abort() }
}

export default api
